import classnames from 'classnames';
import {
  Children,
  cloneElement,
  type FunctionComponent,
  type NamedExoticComponent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { type ButtonProps } from '../Button/Button';

import styles from './ButtonGroup.module.scss';

export interface ButtonGroupProps {
  /**
   * Buttons to group.
   * Will take into account all children with a displayName equal to `Button`.
   */
  children: ReactNode;
  /**
   * ClassName to customize the style
   */
  className?: string;
  /**
   * Identifier for test purpose.
   * Will add a `data-testid` attribute on the root element and also on all items (suffixed by `-item`) if no testId defined on the item level.
   */
  'data-testid'?: string;
  /**
   * Direction to align the buttons
   */
  direction?: 'row' | 'column';
  /**
   * To make items separated by the same margin.
   * Will apply only if isFullWidth is sets to `false` & direction is sets to `row`.
   */
  isAutoMargin?: boolean;
  /**
   * To make items take full avalable width (all items will have the same width).
   * If true, will prevent isAutoMargin to apply.
   */
  isFullWidth?: boolean;
  /**
   * @deprecated Use data-testid instead.
   */
  testId?: string;
}

/**
 * Group a series of buttons together and offers many options to handle the way they are displayed.
 */
export const ButtonGroup: FunctionComponent<ButtonGroupProps> = ({
  children,
  className,
  'data-testid': dataTestId,
  direction = 'row',
  isAutoMargin = false,
  isFullWidth = false,
  testId: oldTestId,
  ...restProps
}) => {
  const testId = dataTestId ?? oldTestId;
  const buttonChildren = Children.toArray(children).filter(
    (child) =>
      ((child as ReactElement)?.type as NamedExoticComponent)?.displayName ===
      'Button',
  ) as ReactElement<ButtonProps<HTMLButtonElement>>[];
  if (!buttonChildren.length) {
    return null;
  }

  const rootClassName = classnames(
    styles.root,
    {
      [styles.row]: direction === 'row',
      [styles.column]: direction === 'column',
      [styles.autoMargin]: direction === 'row' && !isFullWidth && isAutoMargin,
    },
    className,
  );

  const baseItemProps = {
    isFullWidth,
  };

  return (
    <ul className={rootClassName} data-testid={testId} {...restProps}>
      {buttonChildren.map((child, index) => {
        const key = child.props.value?.toString() || child.props.id || index;
        const itemProps = {
          ...baseItemProps,
          'data-testid':
            child.props['data-testid'] ||
            child.props.testId ||
            (testId ? `${testId}-item` : undefined),
        };
        return (
          <li
            className={classnames(styles.item, {
              [styles.fullWidth]: isFullWidth,
            })}
            key={key}
          >
            {cloneElement(child, itemProps)}
          </li>
        );
      })}
    </ul>
  );
};

ButtonGroup.displayName = 'ButtonGroup';
